import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import thunk from 'redux-thunk';
import { createStore, combineReducers, applyMiddleware } from 'redux';
import classNames from 'classnames';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import resultsReducer from '../reducers/searchResults';
import { getErrors } from '../../../shared/redux/selectors/errors';
import errorsReducer from '../../../shared/redux/reducers/errors';
import { fetchSearchResults } from '../actions/searchResults';
import RadioGroup from '../../../shared/components/AriaRadio/RadioGroup';
import Refinements from '../components/Refinements';
import ProductResults from '../components/ProductResults';
import ProductSort from '../components/ProductSort';
import Modal from '../components/Modal';
import EmptySearchResults from '../components/EmptySearchResults';
import IconFilter from '../../../shared/icons/icon-filter.svg';
import IconSort from '../../../shared/icons/icon-sort.svg';
import getValueFromWindow from '../../../shared/helpers/getValueFromWindow';
import Analytics from '../../../analytics/Analytics';
import { sortSearchResults, refinements } from '../selectors';
import AttributeCookies from '../../../shared/components/AttributeSelector/helpers/AttributeCookies';
import createUniqueId from '../../../shared/helpers/createUniqueId';
import Dropdown from '../../../shared/components/Dropdown/Dropdown';


const mapStateToProps = state => ({
  results: sortSearchResults(state),
  refinements: refinements(state),
  inFlight: state.searchResults.inFlight,
  title: state.searchResults.title,
  errors: getErrors(state),
});

class ResultsGrid extends Component {
  static isUserBlockingOnError() {
    return true;
  }

  static getDomId() {
    return 'search_results_grid';
  }

  static getStore() {
    const middleware = [thunk];

    const initialState = {
      searchResults: getValueFromWindow(this.getDomId(), 'searchResults'),
      errors: [],
    };

    return createStore(
      combineReducers({
        searchResults: resultsReducer,
        errors: errorsReducer,
      }),
      initialState,
      applyMiddleware(...middleware),
    );
  }

  constructor(props) {
    super(props);
    this.analytics = new Analytics();
    this.refinementHandler = this.refinementHandler.bind(this);
    this.sortHandler = this.sortHandler.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.displayModal = this.displayModal.bind(this);
    this.getSortComp = this.getSortComp.bind(this);
    this.getSortModalComp = this.getSortModalComp.bind(this);
    this.getRefinements = this.getRefinements.bind(this);
    this.displaySearchResults = this.displaySearchResults.bind(this);
    this.setLastUsedFilter = this.setLastUsedFilter.bind(this);
    this.state = { displayModal: false, modalKey: '', lastUsedFilter: null };

    const attributeCookie = (new AttributeCookies()).getValue();

    this.cacheParam = attributeCookie ? `rebateFilterCache=${encodeURIComponent(attributeCookie)}` : '';

    this.lastUsedFilterRef = React.createRef();
  }

  componentDidMount() {
    const { results, title } = this.props;

    this.analytics.productImpressions(
      results.productIds, title,
    );
  }

  getRefinements(props) {
    const { refinements } = this.props;
    const { lastUsedFilter } = this.state;

    return (
      <Refinements
        {...props}
        focusedFilter={lastUsedFilter}
        focusedFilterRef={this.lastUsedFilterRef}
        refinements={refinements}
        refinementHandler={this.refinementHandler}
        setFocusedFilter={this.setLastUsedFilter}
      />
    );
  }

  getSortComp(props) {
    const { results: { productSort } } = this.props;
    let activeValue = '';

    const options = productSort.options.map(option => ({
      label: option.displayName,
      value: option.url,
    }));

    if (productSort.activeProductSortId) {
      activeValue = productSort.options
        .find(option => option.id === productSort.activeProductSortId).url;
    } else {
      options.unshift({
        label: '',
        value: '',
      });
    }

    return (
      <div className="results-grid__sort-by">
        <div className="results-grid__sort-by-header" id="results-grid-sort-by-header">Sort by</div>
        <Dropdown
          inputLabelledBy="results-grid-sort-by-header"
          onChange={event => this.sortHandler(event.target.value)}
          options={options}
          selectedValue={activeValue}
          theme="resultsGrid"
          {...props}
        />
      </div>
    );
  }

  getSortModalComp(props) {
    const { results: { productSort }, inFlight } = this.props;

    const activeValue = createUniqueId(productSort.activeProductSortId);

    return (
      <RadioGroup
        label="Sort By"
        showLabel={false}
        activeValue={activeValue}
        {...props}
        className={classNames('results-grid__sort-wrapper', props.className)}
      >
        <ProductSort
          productSort={productSort}
          sortHandler={this.sortHandler}
          pendingRequest={inFlight}
        />
      </RadioGroup>
    );
  }

  setLastUsedFilter(filter) {
    this.setState({
      lastUsedFilter: filter,
    });
  }

  refinementHandler(url) {
    const { dispatch, inFlight } = this.props;

    if (inFlight) {
      return;
    }

    const separator = url.indexOf('?') > -1 ? '&' : '?';

    dispatch(fetchSearchResults(`${url}${separator}${this.cacheParam}`));
  }

  sortHandler(optionUrl) {
    const { dispatch, inFlight } = this.props;

    if (inFlight) {
      return;
    }

    const separator = optionUrl.indexOf('?') > -1 ? '&' : '?';

    dispatch(fetchSearchResults(`${optionUrl}${separator}${this.cacheParam}`));

    this.setLastUsedFilter(null);
  }

  closeModal() {
    this.setState({ displayModal: false });
  }

  displayModal(modalKey) {
    this.setState({ displayModal: true, modalKey });
  }

  displaySearchResults() {
    const { results = {}, title = '' } = this.props;

    if (results.count === 0) {
      return <EmptySearchResults />;
    }

    return (
      <div className="results-grid__product-wrapper">
        <ProductResults
          productIds={results.productIds}
          categoryTitle={title}
          analytics={this.analytics}
        />
      </div>
    );
  }

  render() {
    const { modalKey, displayModal } = this.state;
    const { results, title } = this.props;
    const SortComp = this.getSortComp;
    const SortModalComp = this.getSortModalComp;
    const FilterComp = this.getRefinements;

    const modalContent = {
      Filter: {
        icon: IconFilter,
        modalContentFn: () => <FilterComp origin="modal" theme="modal" />,
        title: 'Filter by',
        ctaText: 'Apply filters',
      },
      Sort: {
        icon: IconSort,
        modalContentFn: () => <SortModalComp className="results-grid__sort-wrapper--modal" />,
        title: 'Sort by',
        ctaText: 'Apply sort',
      },
    }[modalKey];

    const IconComponent = modalContent ? modalContent.icon : <></>;

    return (
      <div className="se-tier">
        <div className="se-container">
          <div className="se-row">
            {modalContent && (
              <Modal
                closeModal={this.closeModal}
                displayModal={displayModal}
                labelledBy="results-grid-modal-title"
              >
                <>
                  <div>
                    <div className="results-grid__refinement-header">
                      <IconComponent className="results-grid__refinement-icon results-grid__refinement-icon--modal" />
                      <h2 id="results-grid-modal-title" className="results-grid__modal-title">{modalContent.title}</h2>
                    </div>
                    {modalContent.modalContentFn()}
                  </div>
                  <button className="results-grid__modal-btn" onClick={this.closeModal} type="button">
                    {modalContent.ctaText}
                  </button>
                </>
              </Modal>
            )}
            <div
              aria-labelledby="results-grid-filter-title"
              className="results-grid__refinements"
              role="complementary"
            >
              <div className="results-grid__refinement-header">
                <IconFilter className="results-grid__refinement-icon" />
                <h2 id="results-grid-filter-title" className="results-grid__filter-title">Filter by</h2>
              </div>
              <FilterComp />
            </div>
            <div className="results-grid__results" id="main" role="main">
              <div className="results-grid__header-wrapper">
                <h2 className="results-grid__header-title">{title}</h2>
                <div className="results-grid__subheading">
                  {results.count > 0 && (
                    <div className="results-grid__filter-buttons">
                      <button
                        className="results-grid__filter-button"
                        onClick={() => this.displayModal('Filter')}
                        type="button"
                      >
                        <IconFilter className="results-grid__refinement-icon" />
                        Filter
                      </button>
                      <button
                        className="results-grid__filter-button"
                        onClick={() => this.displayModal('Sort')}
                        type="button"
                      >
                        <IconSort className="results-grid__refinement-icon" />
                        Sort
                      </button>
                    </div>
                  )}
                  <div
                    className="results-grid__header-count"
                    role="status"
                    aria-live="polite"
                  >
                    {`Showing ${results.count} of ${results.count}`}
                    <VisuallyHidden>
                      <span>{` ${title}`}</span>
                    </VisuallyHidden>
                  </div>
                </div>
                {results.count > 0 && <SortComp />}
              </div>
              {this.displaySearchResults()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ResultsGrid.propTypes = {
  results: PropTypes.shape({
    count: PropTypes.number.isRequired,
    productIds: PropTypes.arrayOf(PropTypes.shape({})),
    productSort: PropTypes.shape({
      activeProductSortId: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(PropTypes.shape({
        displayName: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      })).isRequired,
    }),
  }).isRequired,
  refinements: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  title: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  inFlight: PropTypes.bool,
};

ResultsGrid.defaultProps = {
  inFlight: false,
};

export default connect(mapStateToProps)(ResultsGrid);
